import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { sitesApi } from '../api.js';
import type { Site, SiteDomain } from '../types.js';

export default function SiteDetail() {
  const { id } = useParams<{ id: string }>();
  const nav = useNavigate();
  const [data, setData] = useState<{ site: Site; file_paths: string[]; domains: SiteDomain[] } | null>(null);
  const [error, setError] = useState('');

  // Add Custom Domain form state
  const [newDomain, setNewDomain] = useState('');
  const [addingDomain, setAddingDomain] = useState(false);
  const [domainError, setDomainError] = useState('');

  // Replace index.html form state
  const [htmlInput, setHtmlInput] = useState('');
  const [deployingHtml, setDeployingHtml] = useState(false);
  const [htmlMessage, setHtmlMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  async function load() {
    if (!id) return;
    try {
      const r = await sitesApi.get(id);
      setData({ site: r.site, file_paths: r.file_paths, domains: r.domains });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load site');
    }
  }

  useEffect(() => { void load(); }, [id]);

  async function verify(hostname: string) {
    try {
      await sitesApi.verifyDomain(hostname);
      await load();
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Verify failed');
    }
  }

  async function removeDom(hostname: string) {
    if (!confirm(`Remove ${hostname}?`)) return;
    try {
      await sitesApi.removeDomain(hostname);
      await load();
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Remove failed');
    }
  }

  async function removeSite() {
    if (!data || !confirm(`Permanently delete site "${data.site.name}"?`)) return;
    try {
      await sitesApi.delete(data.site.id);
      nav('/sites');
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Delete failed');
    }
  }

  async function addDomain() {
    if (!id || !newDomain.trim()) return;
    setAddingDomain(true);
    setDomainError('');
    try {
      await sitesApi.addDomain(id, newDomain.trim());
      setNewDomain('');
      await load();
    } catch (e) {
      setDomainError(e instanceof Error ? e.message : 'Failed to add domain');
    } finally {
      setAddingDomain(false);
    }
  }

  async function deployHtml() {
    if (!id || !htmlInput.trim()) return;
    setDeployingHtml(true);
    setHtmlMessage(null);
    try {
      const result = await sitesApi.replaceFile(id, { path: 'index.html', data: htmlInput, encoding: 'utf-8' });
      setHtmlMessage({ type: 'success', text: `Deployed: ${result.deployment_url}` });
      setHtmlInput('');
    } catch (e) {
      setHtmlMessage({ type: 'error', text: e instanceof Error ? e.message : 'Deploy failed' });
    } finally {
      setDeployingHtml(false);
    }
  }

  if (error) return <div className="alert alert-error">{error}</div>;
  if (!data) return <p style={{ color: '#94a3b8' }}>Loading…</p>;

  const { site, file_paths, domains } = data;

  return (
    <>
      <div className="page-header">
        <h1 className="page-title">
          {site.name} <small style={{ fontWeight: 400, color: '#64748b' }}>({site.slug})</small>
        </h1>
      </div>

      <div className="card" style={{ marginBottom: '1rem' }}>
        <table>
          <tbody>
            <tr>
              <th style={{ width: '180px' }}>Status</th>
              <td>{site.status}</td>
            </tr>
            <tr>
              <th>Site ID</th>
              <td><code style={{ fontSize: '.85rem' }}>{site.id}</code></td>
            </tr>
            <tr>
              <th>Public URL</th>
              <td>
                <a href={site.branded_url ?? site.host_default_url} target="_blank" rel="noreferrer" style={{ fontFamily: 'monospace', fontSize: '.85rem' }}>
                  {site.branded_url ?? site.host_default_url}
                </a>
                {site.branded_url && (
                  <div style={{ marginTop: '.25rem', fontSize: '.8rem', color: '#64748b' }}>
                    Internal: <a href={site.host_default_url} target="_blank" rel="noreferrer" style={{ fontFamily: 'monospace' }}>{site.host_default_url}</a>
                  </div>
                )}
              </td>
            </tr>
            <tr>
              <th>Client</th>
              <td>{site.client_id ?? '—'}</td>
            </tr>
            <tr>
              <th>Host project</th>
              <td>
                <code style={{ fontSize: '.85rem' }}>{site.host_project_name}</code>
                {' '}<span style={{ fontSize: '.78rem', color: '#64748b' }}>
                  on {site.provider === 'cloudflare' ? 'Cloudflare Pages' : 'Vercel'}
                </span>
              </td>
            </tr>
            <tr>
              <th>Created</th>
              <td>{new Date(site.created_at * 1000).toLocaleString('en-AU')}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <h2 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '.75rem' }}>
        Files ({file_paths.length})
      </h2>
      <div className="card" style={{ marginBottom: '1rem' }}>
        {file_paths.length === 0 ? (
          <p style={{ color: '#94a3b8' }}>No files deployed.</p>
        ) : (
          <ul style={{ margin: 0, paddingLeft: '1.25rem' }}>
            {file_paths.map((p) => (
              <li key={p} style={{ fontFamily: 'monospace', fontSize: '.85rem', padding: '.1rem 0' }}>{p}</li>
            ))}
          </ul>
        )}
      </div>

      <h2 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '.75rem' }}>Custom Domains</h2>
      <div className="card" style={{ marginBottom: '1rem' }}>
        <div style={{ display: 'flex', gap: '.5rem', marginBottom: '.75rem' }}>
          <input
            type="text"
            className="input"
            placeholder="www.your-domain.com"
            value={newDomain}
            onChange={(e) => setNewDomain(e.target.value)}
            disabled={addingDomain}
            onKeyDown={(e) => { if (e.key === 'Enter') void addDomain(); }}
            style={{ flex: 1 }}
          />
          <button
            className="btn btn-primary"
            onClick={() => void addDomain()}
            disabled={addingDomain || !newDomain.trim()}
          >
            {addingDomain ? 'Adding…' : 'Add Domain'}
          </button>
        </div>
        {domainError && <div className="alert alert-error" style={{ marginBottom: '.75rem' }}>{domainError}</div>}
        {domains.length === 0 ? (
          <p style={{ color: '#94a3b8' }}>None.</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Hostname</th>
                <th>Verified</th>
                <th>DNS Required</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {domains.map((d) => (
                <tr key={d.hostname}>
                  <td><code style={{ fontSize: '.85rem' }}>{d.hostname}</code></td>
                  <td>{d.verified ? '✅' : '⏳'}</td>
                  <td>
                    {d.verification_payload
                      ? <pre style={{ margin: 0, fontSize: '.75rem', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '4px', padding: '.4rem', overflowX: 'auto' }}>{JSON.stringify(d.verification_payload, null, 2)}</pre>
                      : '—'}
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <button className="btn btn-secondary btn-sm" onClick={() => void verify(d.hostname)} style={{ marginRight: '.4rem' }}>Re-check</button>
                    <button className="btn btn-danger btn-sm" onClick={() => void removeDom(d.hostname)}>Remove</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <h2 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '.75rem' }}>Replace index.html</h2>
      <div className="card" style={{ marginBottom: '1rem' }}>
        <p style={{ color: '#92400e', background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: '6px', padding: '.6rem .75rem', fontSize: '.85rem', marginBottom: '.75rem' }}>
          ⚠️ This replaces the home page HTML. Other text files are preserved. Binary files (images, etc.) are preserved from the platform's own copy of the site. Use Claude/MCP for complex multi-file edits.
        </p>
        <textarea
          className="input"
          rows={12}
          placeholder="Paste your HTML here"
          value={htmlInput}
          onChange={(e) => setHtmlInput(e.target.value)}
          disabled={deployingHtml}
          style={{ width: '100%', fontFamily: 'monospace', fontSize: '.85rem', resize: 'vertical', marginBottom: '.5rem', boxSizing: 'border-box' }}
        />
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
          <button
            className="btn btn-primary"
            onClick={() => void deployHtml()}
            disabled={deployingHtml || !htmlInput.trim()}
          >
            {deployingHtml ? 'Deploying…' : 'Save & Re-deploy'}
          </button>
          {htmlMessage && (
            <div className={`alert ${htmlMessage.type === 'success' ? 'alert-success' : 'alert-error'}`} style={{ flex: 1, margin: 0 }}>
              {htmlMessage.text}
            </div>
          )}
        </div>
      </div>

      <div style={{ marginTop: '1.5rem' }}>
        <button className="btn btn-danger" onClick={() => void removeSite()}>Delete Site</button>
      </div>
    </>
  );
}
